/* Safe-Shield cart UI. Cart mutations remain owned by Impact and SSDiscountPricing. */
(() => {
  const toast = document.getElementById('ss-cart-toast');
  const submitters = new WeakMap();
  let anchor = null;
  let timer;
  const hideToast = () => {
    clearTimeout(timer);
    if (!toast) return;
    if (toast.contains(document.activeElement) && anchor?.isConnected) anchor.focus({ preventScroll: true });
    toast.hidden = true;
  };
  const scheduleHide = () => {
    clearTimeout(timer);
    if (toast && !toast.matches(':hover') && !toast.contains(document.activeElement)) timer = setTimeout(hideToast, 4500);
  };
  const positionToast = () => {
    if (!toast || toast.hidden) return;
    const viewport = window.visualViewport;
    const left = viewport?.offsetLeft || 0;
    const top = viewport?.offsetTop || 0;
    const width = viewport?.width || innerWidth;
    const height = viewport?.height || innerHeight;
    const box = anchor?.isConnected ? anchor.getBoundingClientRect() : null;
    toast.style.maxWidth = `${Math.max(180, width - 24)}px`;
    const rect = toast.getBoundingClientRect();
    let x = box ? box.left + (box.width - rect.width) / 2 : left + width - rect.width - 16;
    let y = box ? box.top - rect.height - 12 : top + 16;
    if (y < top + 12 && box) y = box.bottom + 12;
    x = Math.max(left + 12, Math.min(x, left + width - rect.width - 12));
    y = Math.max(top + 12, Math.min(y, top + height - rect.height - 12));
    toast.style.left = `${x}px`;
    toast.style.top = `${y}px`;
  };
  document.addEventListener('submit', event => {
    if (event.target instanceof HTMLFormElement && event.submitter) submitters.set(event.target, event.submitter);
    const gift = event.target.querySelector?.('.ss-cart-gift-add[data-ss-added]');
    if (gift && !gift.dataset.ssLabel) {
      gift.dataset.ssLabel = gift.innerHTML;
      gift.innerHTML = `${gift.dataset.ssAdded} <span aria-hidden="true">✓</span>`;
    }
  }, true);
  document.addEventListener('variant:add', event => {
    if (event.detail) event.detail.blockCartDrawerOpening = true;
    const drawer = document.getElementById('cart-drawer');
    // Adding another product never interrupts shopping or closes an already-open cart.
    if (!toast || drawer?.hasAttribute('open')) return;
    const form = event.target.closest?.('form');
    const quickBuy = form?.closest('quick-buy-drawer');
    anchor = quickBuy?.controls?.[0] || submitters.get(form) || form?.querySelector('[type="submit"]') || null;
    const product = event.detail?.items?.[0];
    toast.querySelector('[data-ss-toast-product]').textContent = product?.product_title || product?.title || '';
    toast.hidden = false;
    const announcement = document.getElementById('ss-cart-announcement');
    if (announcement) announcement.textContent = `${toast.querySelector('strong').textContent}: ${product?.product_title || product?.title || ''}`;
    positionToast();
    scheduleHide();
  }, true);
  toast?.addEventListener('click', event => {
    if (event.target.closest('[data-ss-toast-close]')) { hideToast(); return; }
    if (event.target.closest('[data-ss-toast-cart]')) {
      const drawer = document.getElementById('cart-drawer');
      if (typeof drawer?.show === 'function') { event.preventDefault(); hideToast(); drawer.show(); }
    }
  });
  toast?.addEventListener('pointerenter', () => clearTimeout(timer));
  toast?.addEventListener('pointerleave', scheduleHide);
  toast?.addEventListener('focusin', () => clearTimeout(timer));
  toast?.addEventListener('focusout', scheduleHide);
  document.addEventListener('keydown', event => { if (event.key === 'Escape') hideToast(); });
  window.addEventListener('resize', positionToast, { passive: true });
  window.addEventListener('scroll', positionToast, { passive: true });
  window.visualViewport?.addEventListener('resize', positionToast, { passive: true });
  document.addEventListener('click', event => {
    const button = event.target.closest('[data-ss-step]');
    if (!button || button.disabled) return;
    const input = button.parentElement.querySelector('input');
    if (!input || input.disabled) return;
    const previous = input.value;
    if (button.dataset.ssStep === '1') input.stepUp(); else input.stepDown();
    if (input.value !== previous) input.dispatchEvent(new Event('change', { bubbles: true }));
  });
  document.addEventListener('line-item:will-change', event => {
    if (!event.target.matches('.ss-cart-line')) return;
    event.target.setAttribute('aria-busy', 'true');
    event.target.querySelectorAll('input, [data-ss-step]').forEach(el => { el.disabled = true; });
  });
  document.addEventListener('line-item:error', event => {
    const line = event.target;
    if (!line.matches('.ss-cart-line')) return;
    line.removeAttribute('aria-busy');
    const input = line.querySelector('input');
    input.disabled = false;
    line.querySelectorAll('[data-ss-step]').forEach(button => {
      button.disabled = button.dataset.ssStep === '-1' ? +input.value <= +input.min : input.max !== '' && +input.value >= +input.max;
    });
  });
  document.addEventListener('cart:error', event => {
    const error = event.target.querySelector?.('.ss-cart-gift-error');
    if (error) { error.textContent = event.detail.error; error.hidden = false; }
    const gift = event.target.querySelector?.('.ss-cart-gift-add[data-ss-label]');
    if (gift) { gift.innerHTML = gift.dataset.ssLabel; delete gift.dataset.ssLabel; }
  });

  // A free sample is only valid while the cart value reaches its threshold. If the value drops below it
  // (quantity lowered, product removed, discount applied), the sample is removed again.
  let syncing = false;
  const syncSamples = async () => {
    if (syncing) return;
    const totalNode = document.querySelector('[data-ss-cart-total]');
    const total = totalNode ? Number(totalNode.dataset.ssCartTotal) : NaN;
    // Sample money metafields and cart.total_price share Shopify's presentment currency.
    if (!Number.isFinite(total)) return;
    const keys = [...new Set([...document.querySelectorAll('[data-ss-sample-threshold]')]
      .filter(line => Number(line.dataset.ssSampleThreshold) > total)
      .map(line => line.dataset.ssSampleKey))];
    if (!keys.length) return;
    syncing = true;
    try {
      const root = window.Shopify?.routes?.root || '/';
      let cart = null;
      for (const key of keys) {
        const sections = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
        const response = await fetch(`${root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: 0, sections })
        });
        if (!response.ok) return;
        cart = await response.json();
      }
      if (!cart) return;
      if (window.themeVariables?.settings?.pageType === 'cart') { window.location.reload(); return; }
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'line-item:change', cart } }));
    } catch (error) {
      /* the sample stays; checkout rules still apply */
    } finally {
      syncing = false;
    }
  };
  let sampleSyncTimer;
  const scheduleSync = () => {
    clearTimeout(sampleSyncTimer);
    sampleSyncTimer = window.setTimeout(syncSamples, 1500);
  };
  document.addEventListener('cart:change', scheduleSync);
  document.addEventListener('cart:refresh', scheduleSync);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scheduleSync); else scheduleSync();
})();

